//@flow
/**
 * ACSF Graph Utils.
 * These functions create svg elements for displaying data with the ACSF graph.
 * These use the general {@link #utils/svg SVG utils functions.}
 * @module utils/svg/acsf
 */

import { Map } from 'immutable';
import learnerACSFGraph from 'assets/images/acsf-learner-graph.svg';
import { svgToDataUri, svgToPng, addCircle, addLine, addRectangle } from './index';
import * as colors from './../styles/colors';

const skills = ['learning', 'reading', 'writing', 'oralcommunication', 'numeracy'];

/**
 * Contains the coordinates for each skill on the base ACSF graph.
 * @memberof utils/svg/acsf
 */
const cx = [
    ['learning', 159.94],
    ['reading', 246.22],
    ['writing', 332.5],
    ['oralcommunication', 418.78],
    ['numeracy', 505.06]
];

// X coordinates for acsf3shortcourse learners
const acsf3shortcourseCX = [
    ['reading', 246.22],
    ['writing', 332.5],
    ['numeracy', 505.06]
];

/**
 * An immutable map of cx used in the addLines function.
 * @memberof utils/svg/acsf
 */
const cxMap = Map(cx);

/**
 * Contains the coordinates for each level on the base ACSF graph; index corresponds to the profile level.
 * @memberof utils/svg/acsf
 */
const cy = [555.5, 452.09, 348.77, 245.45, 142.13, 39];

/**
 * Maps learner and/or course ACSF profiles to a graph and returns the PNG data URI for the image.
 * @memberof utils/svg/acsf
 */
export async function generateLearnerGraph(learnerResultsArr, courseProfile) {
    let svg = generateSvgNode();

    if (courseProfile) {
        addCourseProfileToSvg(svg, courseProfile);
    }

    if (learnerResultsArr && learnerResultsArr.length > 0) {
        for (let { testLevel, learnerResults } of learnerResultsArr) {
            addProfileToSvg(svg, learnerResults, testLevel === 'acsf3shortcourse');
        }
    }

    return await svgToPng(await svgToDataUri(svg), 845, 610);
}

/**
 * Returns base ACSF graph SVG node.
 * @memberof utils/svg/acsf
 */
export function generateSvgNode() {
    let wrappingDiv = document.createElement('div');
    wrappingDiv.innerHTML = learnerACSFGraph;
    return Array.from(wrappingDiv.children)[0];
}

/**
 * Adds the course profile to the base ACSF graph. This creates a bar graph as used to display course profiles.
 * @memberof utils/svg/acsf
 */
export function addCourseProfileToSvg(svg, courseProfile) {
    for (let [skill, x] of cx) {
        let level = Math.min(5, courseProfile[skill]),
            width = 80,
            y = cy[level],
            height = level * 103.32;
        x -= width / 2;
        addRectangle(svg, `${x}`, `${y}`, `${width}`, `${height}`, colors[skill]).setAttribute('fill', colors[skill]);
    }
}

/**
 * Adds a profile to the base ACSF graph.
 * @memberof utils/svg/acsf
 */
export function addProfileToSvg(svg, learnerResults, isShortCourse) {
    addCircles(svg, learnerResults, isShortCourse);
    !isShortCourse && addLines(svg, learnerResults);
}

/**
 * Adds circles to the graph from the profile provided to show level for each skill.
 * @memberof utils/svg/acsf
 */
function addCircles(svg, learnerResults, isShortCourse) {
    const xCoordinates = isShortCourse ? acsf3shortcourseCX : cx;
    const radius = isShortCourse ? '8' : '5';
    for (let [skill, x] of xCoordinates) {
        let level = learnerResults[skill],
            y = cy[level];
        addCircle(svg, radius, `${x}`, `${y}`, isShortCourse);
    }
}

/**
 * Adds lines between each circle from the profile provided.
 * @memberof utils/svg/acsf
 */
function addLines(svg, learnerResults) {
    cx.forEach(([x2y2Skill], i) => {
        if (i === 0) return;
        let x1y1Skill = skills[i - 1];

        let y1 = cy[learnerResults[x1y1Skill]],
            x1 = cxMap.get(x1y1Skill),
            y2 = cy[learnerResults[x2y2Skill]],
            x2 = cxMap.get(x2y2Skill);
        addLine(svg, `${x1}`, `${y1}`, `${x2}`, `${y2}`).setAttribute('class', 'cls-15');
    });
}
